<template>
  <div :class="$style.panelMarginBotton">
    <div
      v-if="showVanityUrl"
      :class="$style.inputRow"
    >
      <InputValidator
        :config="{
          label: 'Base URL*',
          name: spBaseUrl,
          validate: {
            url: { require_protocol: true },
            required: true,
            isSecureUrl: true,
          },
          attribute: 'urlValue',
          placeholder: 'https://www.website.com',
          value: vanityUrl,
        }"
        @InputValidatorBlur="handleUrlChange"
        @InputValidatorChange="handleUrlChange"
        @InputValidatorModel="handleUrlChange"
      />
    </div>
    <div
      v-if="!hideTokenField"
      :class="$style.inputRow"
    >
      <InputValidator
        :config="apiKeyFieldConfig"
        @InputValidatorBlur="handleTokenChange"
        @InputValidatorChange="handleTokenChange"
        @InputValidatorModel="handleTokenChange"
      />
    </div>
    <div
      v-if="scimConnectionType === SCIM"
      :class="$style.inputRow"
    >
      <InputValidator
        :config="{
          type: 'email',
          label: 'Test User Email*',
          name: 'Test User Email',
          validate: 'required|email',
          attribute: 'testUserEmail',
          placeholder: 'email@domain.com',
          value: testUserEmail,
        }"
        @InputValidatorBlur="handleTestUserEmail"
        @InputValidatorChange="handleTestUserEmail"
        @InputValidatorModel="handleTestUserEmail"
      />
    </div>
    <transition
      :enterActiveClass="$style.slideEnterActive"
      :enterClass="$style.slideEnter"
      :leaveActiveClass="$style.slideLeaveActive"
      :leaveToClass="$style.slideLeaveTo"
    >
      <ScimTestErrorsList
        v-if="errorsList.length"
        :class="$style.panelMarginBotton"
        :errorsList="errorsList"
      />
    </transition>

    <div
      v-if="showCancelButton|| showCtaButton"
      :class="$style.actions"
    >
      <FlatActionButton
        v-if="showCtaButton"
        :action="handleClickActionButton"
        :buttonClass="[
          'btnCTA',
          'btnDefault',
          {
            'btn-primary': !enableActivateButton
          },
        ]"
        :disabled="enableActivateButton"
        :loading="loading"
        :text="actionButtonText"
      />
      <a
        v-if="showCancelButton"
        :class="$style.actionLink"
        href="javascript: void(0);"
        @click.prevent="handleCancelButton"
      >cancel</a>
    </div>
  </div>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { isSecureUrl } from 'client/applications/utils/ApplicationUtils';
import { mapActions, mapState } from 'vuex';
import { requiredValidatedFields, scimConnectionTypes } from 'client/util/Constants/ApplicationConstants';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import IdentityManagementMixin from 'client/applications/mixins/IdentityManagementMixin';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import ScimTestErrorsList from 'client/applications/scim/ScimTestErrorsList.vue';

const { application } = ItemTypeConsts;
const applicationModel = StoreModelMap[application];
export default {
  name: 'ScimSpCredentialsForm',

  components: {
    FlatActionButton,
    InputValidator,
    ScimTestErrorsList,
  },

  mixins: [IdentityManagementMixin],

  inject: ['$validator'],

  props: {
    actionButtonText: {
      type: String,
      default: 'Activate',
    },

    disableActionButton: {
      type: Boolean,
      default: false,
    },
    errorsList: {
      type: Array,
      default: () => [],
    },
    handleActionButton: {
      type: Function,
      default: () => {},
    },
    handleCancelButton: {
      type: Function,
      default: () => {},
    },

    hideTokenField: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    showCancelButton: {
      type: Boolean,
      default: true,
    },

    showCtaButton: {
      type: Boolean,
      default: true,
    },

    showVanityUrl: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const { spApiToken, spBaseUrl } = requiredValidatedFields;
    const { SCIM } = scimConnectionTypes;

    const { isScimActive, scimTokenKey } = this.$store.state[applicationModel];

    const label = `Token Key${isScimActive ? '' : '*'}`;
    const validate = isScimActive ? '' : 'required';

    const apiKeyFieldConfig = {
      type: 'password',
      label,
      name: spApiToken,
      validate,
      attribute: 'keyValue',
      placeholder: '******************',
      value: scimTokenKey,
    };

    return {
      apiKeyFieldConfig,
      disableCta: true,
      spApiToken,
      spBaseUrl,
      SCIM,
    };
  },

  computed: {
    ...mapState(applicationModel, {
      tokenKey: 'scimTokenKey',
      testUserEmail: 'scimTestUserEmail',
    }),

    enableActivateButton() {
      if (this.disableCta || this.disableActionButton) {
        return true;
      }

      if (this.tokenKey === '') {
        return true;
      }

      if (this.vanityUrl === '') {
        return true;
      }

      if (this.testUserEmail === '') {
        return true;
      }

      const { flags } = this.$validator;
      const fields = Object.keys(flags);
      if (fields.length === 0) {
        return true;
      }
      return fields.some(field => flags[field].invalid
        || flags[field].invalid === null);
    },
  },

  beforeMount() {
    isSecureUrl(this.$validator);
  },

  methods: {
    ...mapActions(applicationModel, {
      setTestUserEmail: 'setScimTestUserEmail',
    }),

    handleClickActionButton() {
      const { keyValue, urlValue } = this;
      this.handleActionButton({ keyValue, urlValue });
    },

    handleTestUserEmail(newValue) {
      this.setTestUserEmail(newValue.trim());
      if (this.disableCta) {
        this.disableCta = false;
      }
    },

    handleTokenChange(value) {
      this.setTokenKey(value.trim());
      if (this.disableCta) {
        this.disableCta = false;
      }
    },

    handleUrlChange(value) {
      this.setVanityUrl(value.trim());
      if (this.disableCta) {
        this.disableCta = false;
      }
    },

    reset() {
      this.$validator.reset();
    },
  },
};
</script>

<style module>
@value ( gradientDark2, gradientDark3 ) from '~css/colors.css';

.panelMarginBotton {
  margin-bottom: 20px;
}

.inputRow {
  margin-bottom: 15px;
}

.actions {
  display: inline-flex;
  vertical-align: middle;
}

.actions > * {
  margin-right: 10px !important;
}

.actions > *:last-child {
  margin-right: 0 !important;
}

a.actionLink {
  color: gradientDark3;
  font-size: 12px;
  font-weight: 400;
  margin-left: 15px;
  text-decoration: underline;
}

a.actionLink:focus,
a.actionLink:hover {
  color: gradientDark2;
}
</style>
